import { defineStore } from "pinia";
import { computed, ref } from "vue";

import { MINUTE_IN_MILLISECONDS } from "shared/constants";
import { isSocialPlatformTokenValid } from "shared/helpers/social";
import OrganisationSocialIntegration from "shared/resources/OrganisationSocialIntegration";
import features from "shared/services/features";
import { useUserStore } from "shared/stores/user";
import { type NonNullableKey, type Nullable } from "shared/types";
import { type SocialAccountOption } from "shared/types/social";

export type IntegrationWithId = NonNullableKey<
  OrganisationSocialIntegration,
  "id"
>;

interface PendingCaller {
  reject: (reason: unknown) => void;
  resolve: () => void;
}

const VISIBLE_TO_ALL_TEAMS_ID = 0;

export const REQUIRED_FEATURES = {
  hasTeamWorkspaces: "has_team_workspaces",
} as const;

export const useSocialIntegrationsStore = defineStore(
  "socialIntegrations",
  () => {
    const integrations = ref<OrganisationSocialIntegration[]>([]);
    const refreshedAt = ref(Date.now());

    // callers wait on a deferred rather than their own request, so the newest
    // load settles all of them as soon as it lands, even when an earlier
    // request is slow or never answers
    let pendingCallers: PendingCaller[] = [];
    let pendingLoad: Nullable<Promise<void>> = null;
    let latestLoadId = 0;

    const engageIntegrations = computed(() =>
      integrations.value.filter(
        (integration) => integration.isSocialEngage === true
      )
    );

    const engageTeamIntegrations = computed(() =>
      teamIntegrations({ engageOnly: true })
    );

    const teamIntegrationsRecord = computed(() =>
      Object.fromEntries(
        teamIntegrations({}).map((integration) => [integration.id, integration])
      )
    );

    const expiredIntegrationsRecord = computed(() =>
      Object.fromEntries(
        integrations.value
          .filter((integration) => !isSocialPlatformTokenValid(integration))
          .map((integration) => [integration.id, integration])
      )
    );

    const integrationsBySocialPlatform = computed(() => {
      const grouped: Partial<
        Record<
          OrganisationSocialIntegration["socialPlatform"],
          Record<string, IntegrationWithId>
        >
      > = {};

      integrations.value.forEach((integration) => {
        const platform = integration.socialPlatform;

        if (!grouped[platform]) {
          grouped[platform] = {};
        }

        grouped[platform]![integration.id!] = integration as IntegrationWithId;
      });

      return grouped;
    });

    const engageTeamIntegrationsBySocialPlatform = computed(() => {
      const grouped: Partial<
        Record<
          OrganisationSocialIntegration["socialPlatform"],
          Record<string, IntegrationWithId>
        >
      > = {};

      engageTeamIntegrations.value.forEach((integration) => {
        const platform = integration.socialPlatform;

        grouped[platform] = grouped[platform] ?? {};

        if (integration.id) {
          grouped[platform][integration.id] = integration as IntegrationWithId;
        }
      });

      return grouped;
    });

    const socialAccountOptions = computed<SocialAccountOption[]>(() =>
      engageTeamIntegrations.value.map(toSocialAccountOption)
    );

    const socialAccountOptionsRecord = computed<
      Record<string, SocialAccountOption>
    >(() =>
      Object.fromEntries(
        integrations.value.map((integration) => [
          integration.id,
          toSocialAccountOption(integration),
        ])
      )
    );

    function toSocialAccountOption(
      integration: (typeof integrations.value)[number]
    ): SocialAccountOption {
      return {
        label: integration.label,
        platformIcon: integration.platformIcon(),
        socialPlatform: integration.socialPlatform,
        value: integration.id,
      };
    }

    function teamIntegrations({ engageOnly = false }) {
      const socialIntegrations = engageOnly
        ? engageIntegrations.value
        : integrations.value;

      if (!useUserStore().organisation.organisation_teams) {
        return socialIntegrations;
      }

      const userTeamId = features.has(REQUIRED_FEATURES.hasTeamWorkspaces)
        ? useUserStore().preferences.selected_team_social
        : useUserStore().currentUser.primary_organisation_team_id;

      if (!userTeamId) return [];

      return socialIntegrations.filter(
        (integration) =>
          integration.organisationTeamIds === null ||
          [userTeamId, VISIBLE_TO_ALL_TEAMS_ID].some((teamId) =>
            integration.organisationTeamIds.includes(teamId)
          )
      );
    }

    function setIntegrations(newIntegrations: OrganisationSocialIntegration[]) {
      integrations.value = newIntegrations.map((integration) =>
        integration.dup()
      );

      refreshedAt.value = Date.now();
    }

    function loadIntegrations({ reload }: { reload?: boolean } = {}) {
      if (pendingLoad && !reload) return pendingLoad;

      if (
        integrations.value.length &&
        !reload &&
        refreshedAt.value > Date.now() - 5 * MINUTE_IN_MILLISECONDS
      ) {
        return Promise.resolve();
      }

      latestLoadId += 1;
      const loadId = latestLoadId;

      const { promise, reject, resolve } = Promise.withResolvers<void>();

      pendingCallers.push({ reject, resolve });
      pendingLoad = promise;

      OrganisationSocialIntegration.where({
        archivedAt: { eq: "null" },
        isOutreachAccount: { eq: false },
      })
        .selectExtra(["scheduledPosts", "draftPosts"])
        .per(999)
        .all()
        .then((result) => {
          // a reload may have started while this is in flight, so an older response must not overwrite the newer one
          if (loadId !== latestLoadId) return;

          setIntegrations(result.data);
          resolvePendingCallers();
        })
        .catch((error: unknown) => {
          if (loadId !== latestLoadId) return;

          rejectPendingCallers(error);
        });

      return promise;
    }

    function drainPendingCallers() {
      const settling = pendingCallers;

      pendingCallers = [];
      pendingLoad = null;

      return settling;
    }

    function resolvePendingCallers() {
      drainPendingCallers().forEach(({ resolve }) => resolve());
    }

    function rejectPendingCallers(error: unknown) {
      drainPendingCallers().forEach(({ reject }) => reject(error));
    }

    function filterIntegrationsByType<T extends OrganisationSocialIntegration>(
      type: T["socialPlatform"]
    ): T[] {
      return integrations.value.filter(
        (integration): integration is T => integration.socialPlatform === type
      );
    }

    async function loadArchivedIntegrations(filters = {}) {
      return (
        await OrganisationSocialIntegration.where({
          archivedAt: { not_eq: "null" },
          isOutreachAccount: { eq: false },
          ...filters,
        })
          .per(999)
          .all()
      ).data;
    }

    function hasValidIntegrationsByType(
      type: OrganisationSocialIntegration["socialPlatform"]
    ): boolean {
      const integrationsForType = integrationsBySocialPlatform.value[type];
      if (!integrationsForType) return false;

      return Object.values(integrationsForType).some((integration) =>
        isSocialPlatformTokenValid(integration)
      );
    }

    function canCurrentTeamAccessIntegration(integrationId: string): boolean {
      return Boolean(teamIntegrationsRecord.value[integrationId]);
    }

    function hasTeamAccessToValidIntegrations(
      type: OrganisationSocialIntegration["socialPlatform"]
    ): boolean {
      // if we don't have the feature enabled then we don't need to check
      // if the user has any team integrations - just allow it.
      if (!features.has(REQUIRED_FEATURES.hasTeamWorkspaces)) return true;

      // Check if there are any valid integrations of this type that the user's team can access
      // integrationsBySocialPlatform now includes expired integrations, so we need to filter them out
      // and teamIntegrationsRecord contains only integrations the user's team can access
      const integrationsForType = integrationsBySocialPlatform.value[type];

      if (!integrationsForType) return false;

      return Object.keys(integrationsForType).some((integrationId) => {
        const integration = integrationsForType[integrationId];

        return (
          isSocialPlatformTokenValid(integration) &&
          canCurrentTeamAccessIntegration(integrationId)
        );
      });
    }

    function hasTeamAccessToValidIntegration(
      integration: OrganisationSocialIntegration
    ): boolean {
      if (!isSocialPlatformTokenValid(integration)) return false;

      // Check if integration has an ID (required for team access check)
      if (!integration.id) return false;

      // if we don't have the feature enabled then we don't need to check
      // if the user has any team integrations - just allow it.
      if (!features.has(REQUIRED_FEATURES.hasTeamWorkspaces)) return true;

      return canCurrentTeamAccessIntegration(integration.id);
    }

    return {
      engageIntegrations,
      engageTeamIntegrations,
      engageTeamIntegrationsBySocialPlatform,
      expiredIntegrationsRecord,
      filterIntegrationsByType,
      hasTeamAccessToValidIntegration,
      hasTeamAccessToValidIntegrations,
      hasValidIntegrationsByType,
      integrations,

      integrationsBySocialPlatform,
      loadArchivedIntegrations,
      loadIntegrations,
      refreshedAt,
      setIntegrations,
      socialAccountOptions,
      socialAccountOptionsRecord,
      teamIntegrations,
      teamIntegrationsRecord,
    };
  }
);
